import type { Event, EventHint } from '@sentry/nextjs';

const FILTERED_HTTP_STATUS_CODES = new Set([400, 401, 404, 422]);
const NOISY_EXTERNAL_DOMAINS = [
  /(^|\.)googletagmanager\.com$/i,
  /(^|\.)google-analytics\.com$/i,
  /(^|\.)analytics\.google\.com$/i,
];

// FIN-7800: o WebKit usa essa mensagem quando a navegação aborta operações de
// I/O ainda em voo. Só descartamos o evento se ele não foi tratado
// (onunhandledrejection) e nenhum frame first-party aparece no stacktrace.
// Os filenames chegam ao beforeSend já reescritos pelo rewriteFrames do
// @sentry/nextjs (app:///_next/static/...). No WebKit essas rejeições costumam
// vir sem frames JS, então a guarda first-party vale sobretudo para outros
// navegadores. O padrão /_next/static/ também casa os chunks do framework
// (React/Next), o que mantém o evento: lado conservador, intencional.
const WEBKIT_NAVIGATION_ABORT_MESSAGE = 'the i/o read operation failed';
const FIRST_PARTY_FRAME_PATTERNS = [
  /\/_next\/static\//i,
  /^webpack-internal:\/\//i,
];

const toStatusCode = (value: unknown): number | undefined => {
  if (typeof value === 'number') {
    return Number.isInteger(value) ? value : undefined;
  }

  if (typeof value !== 'string') {
    return undefined;
  }

  const parsed = Number(value);
  return Number.isInteger(parsed) ? parsed : undefined;
};

const extractStatusCode = (
  event: Event,
  hint: EventHint
): number | undefined => {
  const originalException = hint.originalException as {
    response?: { status?: unknown };
    status?: unknown;
  };

  const candidates = [
    originalException?.response?.status,
    originalException?.status,
    event.tags?.statusCode,
    event.contexts?.http?.statusCode,
  ];

  for (const candidate of candidates) {
    const statusCode = toStatusCode(candidate);
    if (statusCode) {
      return statusCode;
    }
  }

  return undefined;
};

const extractMessage = (event: Event, hint: EventHint): string => {
  const originalException = hint.originalException as
    | { message?: string }
    | string;
  const exceptionValues = event.exception?.values || [];
  const exceptionMessages = exceptionValues
    .flatMap((value) => [value.type, value.value])
    .filter((value): value is string => typeof value === 'string');

  const originalExceptionMessage =
    typeof originalException === 'string'
      ? originalException
      : originalException?.message;

  return [event.message, ...exceptionMessages, originalExceptionMessage]
    .filter((value): value is string => typeof value === 'string')
    .join(' ')
    .toLowerCase();
};

const extractRequestUrl = (
  event: Event,
  hint: EventHint
): string | undefined => {
  const originalException = hint.originalException as {
    config?: { url?: unknown };
  };

  const candidates = [
    originalException?.config?.url,
    event.contexts?.http?.requestUrl,
    event.request?.url,
  ];

  return candidates.find(
    (candidate): candidate is string =>
      typeof candidate === 'string' && candidate.length > 0
  );
};

const parseRequestUrl = (requestUrl: string): URL | undefined => {
  try {
    const base =
      typeof window !== 'undefined'
        ? window.location.origin
        : 'http://localhost';
    return new URL(requestUrl, base);
  } catch {
    return undefined;
  }
};

const shouldDropExpectedClientApiError = (
  statusCode: number | undefined
): boolean => {
  return Boolean(statusCode && FILTERED_HTTP_STATUS_CODES.has(statusCode));
};

const isKnownNoisyExternalUrl = (requestUrl: string): boolean => {
  const parsedUrl = parseRequestUrl(requestUrl);
  if (!parsedUrl) return false;
  return NOISY_EXTERNAL_DOMAINS.some((pattern) =>
    pattern.test(parsedUrl.hostname)
  );
};

const hasFirstPartyFrame = (event: Event): boolean => {
  return Boolean(
    event.exception?.values?.some((exception) =>
      exception.stacktrace?.frames?.some((frame) => {
        const filename = frame.filename;
        return (
          typeof filename === 'string' &&
          FIRST_PARTY_FRAME_PATTERNS.some((pattern) => pattern.test(filename))
        );
      })
    )
  );
};

const isUnhandledException = (event: Event): boolean => {
  return Boolean(
    event.exception?.values?.some(
      (exception) => exception.mechanism?.handled === false
    )
  );
};

const isWebKitNavigationAbort = (message: string): boolean => {
  return message.includes(WEBKIT_NAVIGATION_ABORT_MESSAGE);
};

export const shouldDropSentryEvent = (
  event: Event,
  hint: EventHint
): boolean => {
  const statusCode = extractStatusCode(event, hint);
  const requestUrl = extractRequestUrl(event, hint);

  if (shouldDropExpectedClientApiError(statusCode)) {
    return true;
  }

  const message = extractMessage(event, hint);

  if (
    isWebKitNavigationAbort(message) &&
    isUnhandledException(event) &&
    !hasFirstPartyFrame(event)
  ) {
    return true;
  }

  if (
    message.includes('failed to fetch') &&
    requestUrl &&
    isKnownNoisyExternalUrl(requestUrl)
  ) {
    return true;
  }

  return false;
};
